import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  getRecurringInvoices, 
  getClients, 
  getProducts,
  createRecurringInvoice, 
  updateRecurringInvoice,
  deleteRecurringInvoice,
  generateFromRecurring 
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  MoreHorizontal, 
  Repeat, 
  Play,
  Pause,
  Trash2,
  Pencil,
  RefreshCw,
  Calendar,
  User,
  Loader2,
  Zap,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const RecurringInvoicesPage = () => {
  const navigate = useNavigate();
  const [recurrings, setRecurrings] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    frequency: 'monthly',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    currency: 'CHF',
    vat_enabled: true,
    auto_send: false,
    payment_terms: '',
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0, vat_rate: 7.7, discount_percent: 0 }]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recurringRes, clientsRes, productsRes] = await Promise.all([
        getRecurringInvoices(),
        getClients(),
        getProducts()
      ]);
      setRecurrings(recurringRes.data);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      frequency: 'monthly',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
      currency: 'CHF',
      vat_enabled: true,
      auto_send: false,
      payment_terms: '',
      notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0, vat_rate: 7.7, discount_percent: 0 }]
    });
    setEditingRecurring(null);
  };

  const handleEdit = (recurring) => {
    setEditingRecurring(recurring);
    setFormData({
      client_id: recurring.client_id,
      frequency: recurring.frequency,
      start_date: recurring.start_date,
      end_date: recurring.end_date || '',
      currency: recurring.currency,
      vat_enabled: recurring.vat_enabled,
      auto_send: recurring.auto_send,
      payment_terms: recurring.payment_terms || '',
      notes: recurring.notes || '',
      items: recurring.items
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (formData.items.some(item => !item.description || item.quantity <= 0)) {
      toast.error('Veuillez remplir tous les articles');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...formData,
        end_date: formData.end_date || null
      };

      if (editingRecurring) {
        await updateRecurringInvoice(editingRecurring.id, data);
        toast.success('Facture récurrente mise à jour');
      } else {
        await createRecurringInvoice(data);
        toast.success('Facture récurrente créée');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture récurrente ?')) return;
    try {
      await deleteRecurringInvoice(id);
      toast.success('Supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleActive = async (recurring) => {
    try {
      await updateRecurringInvoice(recurring.id, { active: !recurring.active });
      toast.success(recurring.active ? 'Désactivée' : 'Activée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleGenerate = async (id) => {
    setGeneratingId(id);
    try {
      const response = await generateFromRecurring(id);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingId(null);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, vat_rate: 7.7, discount_percent: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const selectProduct = (index, productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      updateItem(index, 'description', product.name);
      updateItem(index, 'unit_price', product.price);
      if (product.vat_rate) updateItem(index, 'vat_rate', product.vat_rate);
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const subtotal = item.quantity * item.unit_price;
      const discount = subtotal * (item.discount_percent || 0) / 100;
      const afterDiscount = subtotal - discount;
      const vat = formData.vat_enabled ? afterDiscount * (item.vat_rate || 0) / 100 : 0;
      return sum + afterDiscount + vat;
    }, 0);
  };

  const frequencyLabels = {
    monthly: 'Mensuelle',
    quarterly: 'Trimestrielle',
    yearly: 'Annuelle'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="recurring-invoices-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">
            Factures <span className="text-blue-600 dark:text-[#00F0FF]">récurrentes</span>
          </h1>
          <p className="text-slate-500 dark:text-zinc-400">Automatisez vos facturations mensuelles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 dark:bg-[#00F0FF] dark:text-black dark:hover:bg-[#00F0FF]/90" data-testid="new-recurring-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle récurrence
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-[#111113] dark:border-white/10">
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {editingRecurring ? 'Modifier la récurrence' : 'Nouvelle facture récurrente'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              {/* Client & Frequency */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-zinc-300">Client *</Label>
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                    <SelectTrigger data-testid="recurring-client-select" className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#1a1a1c] dark:border-white/10">
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id} className="dark:text-white dark:hover:bg-white/5">
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="dark:text-zinc-300">Fréquence *</Label>
                  <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                    <SelectTrigger data-testid="recurring-frequency-select" className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#1a1a1c] dark:border-white/10">
                      <SelectItem value="monthly" className="dark:text-white dark:hover:bg-white/5">Mensuelle</SelectItem>
                      <SelectItem value="quarterly" className="dark:text-white dark:hover:bg-white/5">Trimestrielle</SelectItem>
                      <SelectItem value="yearly" className="dark:text-white dark:hover:bg-white/5">Annuelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-zinc-300">Date de début *</Label>
                  <Input 
                    type="date" 
                    value={formData.start_date} 
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white"
                    data-testid="recurring-start-date"
                  />
                </div>
                <div>
                  <Label className="dark:text-zinc-300">Date de fin (optionnel)</Label>
                  <Input 
                    type="date" 
                    value={formData.end_date} 
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white"
                    data-testid="recurring-end-date"
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <Label className="dark:text-zinc-300">Articles</Label>
                <div className="space-y-3 mt-2">
                  {formData.items.map((item, index) => (
                    <div key={item.id || `item-${index}`} className="p-3 bg-slate-50 dark:bg-[#1a1a1c] rounded-lg space-y-2">
                      <div className="flex gap-2">
                        <Select onValueChange={(v) => selectProduct(index, v)}>
                          <SelectTrigger className="w-[150px] dark:bg-[#111113] dark:border-white/10 dark:text-white">
                            <SelectValue placeholder="Produit..." />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-[#1a1a1c] dark:border-white/10">
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id} className="dark:text-white dark:hover:bg-white/5">{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input 
                          value={item.description} 
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Description"
                          className="flex-1 dark:bg-[#111113] dark:border-white/10 dark:text-white"
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <Input 
                          type="number" 
                          value={item.quantity} 
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                          placeholder="Qté"
                          min="1"
                          className="dark:bg-[#111113] dark:border-white/10 dark:text-white"
                        />
                        <Input 
                          type="number" 
                          value={item.unit_price} 
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value))}
                          placeholder="Prix unit."
                          step="0.01"
                          className="dark:bg-[#111113] dark:border-white/10 dark:text-white"
                        />
                        <Input 
                          type="number" 
                          value={item.vat_rate} 
                          onChange={(e) => updateItem(index, 'vat_rate', parseFloat(e.target.value))}
                          placeholder="TVA %"
                          step="0.1"
                          className="dark:bg-[#111113] dark:border-white/10 dark:text-white"
                        />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeItem(index)}
                          className="text-red-500"
                          disabled={formData.items.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addItem} className="mt-2 dark:border-white/10 dark:text-white dark:hover:bg-white/5">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter un article
                </Button>
              </div>

              {/* Options */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-[#1a1a1c] rounded-lg">
                  <Label className="dark:text-zinc-300">Avec TVA</Label>
                  <Switch checked={formData.vat_enabled} onCheckedChange={(v) => setFormData({ ...formData, vat_enabled: v })} />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-[#1a1a1c] rounded-lg">
                  <Label className="dark:text-zinc-300">Envoi auto</Label>
                  <Switch checked={formData.auto_send} onCheckedChange={(v) => setFormData({ ...formData, auto_send: v })} />
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="dark:text-zinc-300">Notes (optionnel)</Label>
                <Textarea 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes apparaissant sur la facture..."
                  rows={2}
                  className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white"
                />
              </div>

              {/* Total */}
              <div className="p-4 bg-blue-50 dark:bg-[#00F0FF]/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium dark:text-white">Total estimé par facture</span>
                  <span className="text-xl font-bold text-blue-600 dark:text-[#00F0FF]">
                    {calculateTotal().toFixed(2)} {formData.currency}
                  </span>
                </div>
              </div>

              {/* Submit */}
              <Button 
                onClick={handleSubmit} 
                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-[#00F0FF] dark:text-black"
                disabled={submitting}
                data-testid="save-recurring-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {editingRecurring ? 'Mettre à jour' : 'Créer la récurrence'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="dark:bg-[#111113] dark:border-white/10">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-[#00F0FF]/10 flex items-center justify-center">
              <Repeat className="w-6 h-6 text-blue-600 dark:text-[#00F0FF]" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white">{recurrings.length}</p>
              <p className="text-sm text-slate-500 dark:text-zinc-400">Récurrences</p>
            </div>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#111113] dark:border-white/10">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-[#39FF14]/10 flex items-center justify-center">
              <Play className="w-6 h-6 text-green-600 dark:text-[#39FF14]" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white">{recurrings.filter(r => r.active).length}</p>
              <p className="text-sm text-slate-500 dark:text-zinc-400">Actives</p>
            </div>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#111113] dark:border-white/10">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-[#BF00FF]/10 flex items-center justify-center">
              <Zap className="w-6 h-6 text-purple-600 dark:text-[#BF00FF]" />
            </div>
            <div>
              <p className="text-2xl font-bold dark:text-white">{recurrings.reduce((sum, r) => sum + r.generated_count, 0)}</p>
              <p className="text-sm text-slate-500 dark:text-zinc-400">Factures générées</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* List */}
      <motion.div variants={itemVariants}>
        {recurrings.length === 0 ? (
          <Card className="dark:bg-[#111113] dark:border-white/10">
            <CardContent className="py-12 text-center">
              <Repeat className="w-12 h-12 text-slate-300 dark:text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold dark:text-white mb-2">Aucune facture récurrente</h3>
              <p className="text-slate-500 dark:text-zinc-400 mb-4">Créez une récurrence pour automatiser vos facturations</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 dark:bg-[#00F0FF] dark:text-black">
                <Plus className="w-4 h-4 mr-2" /> Créer une récurrence
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {recurrings.map((recurring) => (
              <Card key={recurring.id} className={`dark:bg-[#111113] dark:border-white/10 ${!recurring.active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${recurring.active ? 'bg-green-100 dark:bg-[#39FF14]/10' : 'bg-slate-100 dark:bg-zinc-800'}`}>
                        <Repeat className={`w-6 h-6 ${recurring.active ? 'text-green-600 dark:text-[#39FF14]' : 'text-slate-400 dark:text-zinc-500'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold dark:text-white">{recurring.client?.name || 'Client inconnu'}</h3>
                          <Badge variant={recurring.active ? 'default' : 'secondary'} className={recurring.active ? 'bg-green-100 text-green-700 dark:bg-[#39FF14]/20 dark:text-[#39FF14]' : ''}>
                            {recurring.active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline" className="dark:border-white/20 dark:text-zinc-300">
                            {frequencyLabels[recurring.frequency]}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-zinc-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Prochaine: {format(new Date(recurring.next_generation), 'dd MMM yyyy', { locale: fr })}
                          </span>
                          <span>{recurring.generated_count} générée(s)</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold dark:text-white">
                          {recurring.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0).toFixed(2)} {recurring.currency}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">par facture</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerate(recurring.id)}
                        disabled={generatingId === recurring.id || !recurring.active}
                        className="dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                        data-testid={`generate-btn-${recurring.id}`}
                      >
                        {generatingId === recurring.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="dark:hover:bg-white/5">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="dark:bg-[#1a1a1c] dark:border-white/10">
                          <DropdownMenuItem onClick={() => handleEdit(recurring)} className="dark:text-white dark:hover:bg-white/5">
                            <Pencil className="w-4 h-4 mr-2" /> Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(recurring)} className="dark:text-white dark:hover:bg-white/5">
                            {recurring.active ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                            {recurring.active ? 'Désactiver' : 'Activer'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="dark:bg-white/10" />
                          <DropdownMenuItem onClick={() => handleDelete(recurring.id)} className="text-red-600 dark:text-red-400">
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default RecurringInvoicesPage;
